import React, { useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import {
  matchPropTypes,
  breadcrumbsPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import { getItineraryIncidentById } from '../../../../actions/supervision-control/IncidentRecord';
import {
  generateITineraryIncidentPath,
  ITINERARY_INCIDENT_PATH,
  SUPERVISION_CONTROL_UNIT_PATH,
} from '../../../../config/paths';
import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import ResourceProperty from '../../../common/resource/ResourceProperty';
import { tzNormalizeDate } from '../../../../utils/date';
import { DATE_TIME_FORMAT } from '../../../../config/locale';
import { INCIDENT_RECORD_TYPE } from '../../../../config/constants';
import EnumStatus from '../../../common/EnumStatus';

const ItineraryIncidentRecord = ({
  match: {
    params: { incidentRecordId },
  },
  dispatchGetItineraryIncidentById,
  breadcrumbs,
  loading,
  incidentRecord,
}) => {
  useLayoutEffect(() => {
    dispatchGetItineraryIncidentById({ incidentRecordId });
  }, []);

  const buildTree = (answers) => {
    const buildNode = (node) => {
      const children = answers
        .filter((a) => a.parentId === node.nodeId)
        .map((child) => buildNode(child));
      return { ...node, children };
    };

    return answers
      .filter((a) => a.parentId === null)
      .map((root) => buildNode(root));
  };

  const flattenTree = (nodes, depth = 0) =>
    nodes.flatMap((node) => [
      { ...node, depth },
      ...flattenTree(node.children || [], depth + 1),
    ]);
  let content = null;

  if (loading) {
    content = <Loader />;
  } else if (incidentRecord.isEmpty()) {
    content = <NoDataResource returnPage={SUPERVISION_CONTROL_UNIT_PATH} />;
  } else {
    const incidentRecordJS = incidentRecord.toJS();
    const {
      answers,
      itinerary,
      userFullName,
      incidentDate,
      incidentRecordType,
    } = incidentRecordJS;

    const trees = buildTree(answers);

    content = (
      <>
        <ResourceProperty label="Usuario:">{userFullName}</ResourceProperty>
        <ResourceProperty label="Tipo de Registro:">
          <EnumStatus
            enumValue={incidentRecordType}
            enumObject={INCIDENT_RECORD_TYPE}
          />
        </ResourceProperty>
        <ResourceProperty label="Fecha del Incidente:">
          {tzNormalizeDate({ date: incidentDate, format: DATE_TIME_FORMAT })}
        </ResourceProperty>
        <ResourceProperty label="ID del Itinerario:">
          {itinerary.id}
        </ResourceProperty>

        <h4>Selecciones</h4>

        {trees.map((root) => {
          const flatNodes = flattenTree([root]);
          return (
            <div key={root.nodeId} style={{ marginBottom: '1rem' }}>
              <ResourceProperty label={`${root.label}:`}>
                <ul style={{ marginBottom: 0 }}>
                  {flatNodes.slice(1).map((node) => (
                    <li
                      key={node.nodeId}
                      style={{ marginLeft: `${(node.depth - 1) * 16}px` }}
                    >
                      <strong>{node.label}</strong>
                      {node.note && (
                        <span style={{ color: '#666', marginLeft: 8 }}>
                          — {node.note}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </ResourceProperty>
            </div>
          );
        })}
      </>
    );
  }

  return (
    <Content title="Incidente" breadcrumbs={breadcrumbs} content={content} />
  );
};

ItineraryIncidentRecord.propTypes = {
  match: matchPropTypes.isRequired,
  dispatchGetItineraryIncidentById: PropTypes.func.isRequired,
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  loading: PropTypes.bool,
  incidentRecord: PropTypes.instanceOf(Immutable.Map).isRequired,
};

ItineraryIncidentRecord.defaultProps = {
  loading: false,
};

const mapStateToProps = (
  { SupervisionControlUnit },
  {
    match: {
      params: { itineraryId, segmentId },
    },
  },
) => ({
  breadcrumbs: [
    ...SupervisionControlUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Incidentes en Itinerarios',
      href: ITINERARY_INCIDENT_PATH,
    },
    {
      text: 'Incidente en Itinerario',
      href: generateITineraryIncidentPath({
        itineraryId,
        segmentId,
      }),
    },
    {
      text: 'Incidente',
      href: '',
    },
  ],
  loading: SupervisionControlUnit.IncidentRecord.getIn([
    'incidentRecord',
    'loading',
  ]),
  incidentRecord: SupervisionControlUnit.IncidentRecord.getIn([
    'incidentRecord',
    'content',
  ]),
});

const mapDispatchToProps = {
  dispatchGetItineraryIncidentById: getItineraryIncidentById,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ItineraryIncidentRecord);
